import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { selectRole } from "state/auth/authSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { AbilityContext } from "context/Ability.context";
import navRoutes from "navRoutes";
import { isStandalonePath } from "utils/standalonePages";
import { useIsMobile } from "utils/hooks/useBreakpoint";

import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import adaptNavRoutes from "./navAdapter";

const MINI_KEY = "rec.shell.mini";

function readMini() {
  try {
    return localStorage.getItem(MINI_KEY) === "1";
  } catch (e) {
    return false;
  }
}

function writeMini(value) {
  try {
    localStorage.setItem(MINI_KEY, value ? "1" : "0");
  } catch (e) {
    // storage unavailable; non-fatal
  }
}

export default function Layout({ children }) {
  const { t } = useTranslation(["sidenav", "common"]);
  const { pathname } = useLocation();
  const ability = useContext(AbilityContext);
  const role = useSelector(selectRole);
  const community = useSelector(selectCurrentCommunity);
  const isMobile = useIsMobile();

  const [mini, setMini] = useState(readMini);
  const [mobShow, setMobShow] = useState(false);

  useEffect(() => {
    writeMini(mini);
  }, [mini]);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setMobShow((s) => !s);
    } else {
      setMini((m) => !m);
    }
  }, [isMobile]);

  // Close the mobile drawer whenever the route changes (e.g. back button,
  // programmatic navigation) — not only when a nav item is tapped.
  useEffect(() => {
    setMobShow(false);
  }, [pathname]);

  // Esc closes the mobile drawer.
  useEffect(() => {
    if (!mobShow) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobShow(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobShow]);

  // Lock body scroll while the mobile drawer is open so the page behind it
  // does not scroll under the overlay.
  useEffect(() => {
    if (!(isMobile && mobShow)) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, mobShow]);

  const nav = useMemo(
    () =>
      community && ability
        ? adaptNavRoutes(
            navRoutes(
              t,
              ability,
              community.id,
              community.type,
              community.community_or_aggregator_type
            )
          )
        : [],
    [community, ability, t]
  );

  const showSidebar = !!role;

  // Standalone full-page views carry their own chrome; render them bare even
  // if this layout ends up wrapping them.
  if (isStandalonePath(pathname)) {
    return children;
  }

  return (
    <div className="rec-app">
      {showSidebar && (
        <Sidebar nav={nav} mini={mini} show={mobShow} onClose={() => setMobShow(false)} />
      )}
      {showSidebar && isMobile && mobShow && (
        <div className="rec-backdrop" role="presentation" onClick={() => setMobShow(false)} />
      )}
      <div className="rec-appbody">
        {showSidebar && <Topbar onToggleSidebar={toggleSidebar} />}
        <main className="rec-content">{children}</main>
      </div>
    </div>
  );
}

Layout.propTypes = {
  children: PropTypes.node,
};

Layout.defaultProps = {
  children: null,
};
